// Patent claims exercised: P-005 Claims 7-8,11-12 (shopping list generation, meal planning)
import { API_BASE } from "../../config";
import { formatDateOnly } from '../../utils/dates';
import React, { useState, useEffect } from 'react';
import MedicalDisclaimer from '../MedicalDisclaimer';
import { handleAIError } from '../../utils/aiErrorHandler';
import CostChip from '../CostChip';
import { useCreditSpend } from '../../contexts/CreditSpendContext';

const MealPlanGenerator = ({ user, token, fetchWithAuth, onClose, onBack, onSuccess, onPromptData }) => {
  // Auth-aware fetch with 401 retry (falls back to raw fetch if fetchWithAuth not provided)
  const authFetch = fetchWithAuth || ((url, opts) => fetch(url, { ...opts, headers: { ...opts?.headers, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }));
  const { spend } = useCreditSpend();
  // #77 cost is driven by duration: 1-3d=meal_plan_3day(15), 4-7d=7day(35), 8+d=14day(80).
  const mealPlanApiType = (d) => d <= 3 ? 'meal_plan_3day' : d <= 7 ? 'meal_plan_7day' : 'meal_plan_14day';
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userPreferences, setUserPreferences] = useState(null);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Starting generation...');
  const [fitnessPrograms, setFitnessPrograms] = useState([]);
  /* Card-by-card fill status after a skeleton plan is delivered. null = not in that state. */
  const [cardProgress, setCardProgress] = useState(null);

  const [formData, setFormData] = useState({
    // Basic fields
    plan_name: '',
    plan_type: 'maintenance',
    duration_days: 7,
    meals_per_day: 3,
    snacks_per_day: 2,
    variety: 1.0,
    
    // Calorie framework
    target_calories_daily: 2200,
    use_bmr: false,
    bmr_baseline: null,
    activity_multiplier: 1.5,
    calorie_adjustment_goal: 0,
    
    // Macros
    macro_method: 'percentage', // 'percentage' or 'grams'
    target_protein_percentage: 30,
    target_carbs_percentage: 40,
    target_fat_percentage: 30,
    target_protein_grams: null,
    target_carbs_grams: null,
    target_fat_grams: null,
    
    // Micronutrients
    include_micros: false,
    target_fiber_grams: 30,
    target_sodium_mg: 2300,
    target_potassium_mg: 3500,
    target_calcium_mg: 1000,
    target_iron_mg: 18,
    target_vitamin_d_iu: 600,
    target_vitamin_c_mg: 90,
    
    // Diet & preferences
    diet_category: 'standard_balanced',
    use_saved_preferences: true,
    cuisine_preferences: ['no_preference'],
    // DEFAULT = 'prefer_existing' (Vincent, 2026-10-06: "default to mix but user can still change
    // in UI"). This is the capacity decision made as a product decision: 'prefer_existing' routes
    // to the catalog and generates only the gaps, so the common case reserves one small call
    // instead of 2 x 45,000 tokens. Was 'none' (generate all 70 recipes) until today.
    // Every other value remains one click away in the Recipe Source control below.
    recipe_source_preference: 'prefer_existing',
    
    // Meal timing
    meal_timing_strategy: 'flexible',
    eating_window_start: '08:00',
    eating_window_end: '20:00',
    
    // Hydration & monitoring
    target_water_ml: 2500,
    electrolyte_needs: false,
    meal_prep_batch_size: 7,
    monitor_weight: true,
    monitor_body_composition: true,
    monitor_energy_levels: true,
    monitor_digestion: false,
    recalibration_frequency_days: 14,
    
    // Status
    is_active: true,
    start_date: new Date().toISOString().split('T')[0],
    end_date: null, // Will be calculated
    // Fitness Program Link
    fitness_program_id: null
  });

  useEffect(() => {
    loadUserPreferences();
    loadFitnessPrograms();
    calculateEndDate();
  }, [formData.duration_days, formData.start_date]);

  /* CARD FILL PROGRESS — poll while the server fills recipe cards behind a delivered plan.
     The fill runs SERVER-SIDE (a detached task started after the `complete` event), so closing
     this panel does not cancel it; the panel only reports. Polling rather than a second SSE
     stream because the payload is tiny and a dropped poll is harmless, where a dropped stream
     would need reconnect logic for no benefit. */
  useEffect(() => {
    if (!cardProgress?.planId) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await authFetch(`${API_BASE}/api/meal-plans/${cardProgress.planId}/cards/status`);
        if (!r.ok) return;
        const d = await r.json();
        if (stop) return;
        setCardProgress((prev) => (prev ? { ...prev, total: d.total, ready: d.cards_ready,
                                            complete: d.complete, items: d.items || [] } : prev));
      } catch (e) {
        /* A failed poll is not a failure of the plan. Keep polling; the next one may succeed. */
      }
    };
    tick();
    const id = setInterval(tick, 2500);
    return () => { stop = true; clearInterval(id); };
  }, [cardProgress?.planId]);

  const loadUserPreferences = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/health/preferences`);
      if (response.ok) {
        const prefs = await response.json();
        setUserPreferences(prefs);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
  };

  const loadFitnessPrograms = async () => {
    try {
      // Only fetch active programs that don't already have a meal plan
      const response = await authFetch(`${API_BASE}/api/fitness-programs/list?without_meal_plan=true`);
      if (response.ok) {
        const data = await response.json();
        setFitnessPrograms(data.programs || []);
      }
    } catch (error) {
      console.error('Failed to load fitness programs:', error);
    }
  };

  const calculateEndDate = () => {
    if (formData.start_date && formData.duration_days) {
      const start = new Date(formData.start_date);
      const end = new Date(start);
      end.setDate(end.getDate() + formData.duration_days);
      setFormData(prev => ({ ...prev, end_date: end.toISOString().split('T')[0] }));
    }
  };

  const calculateMacros = () => {
    const cals = formData.target_calories_daily;
    if (formData.macro_method === 'percentage') {
      const protein_g = (cals * (formData.target_protein_percentage / 100)) / 4;
      const carbs_g = (cals * (formData.target_carbs_percentage / 100)) / 4;
      const fat_g = (cals * (formData.target_fat_percentage / 100)) / 9;
      return {
        protein: Math.round(protein_g),
        carbs: Math.round(carbs_g),
        fat: Math.round(fat_g)
      };
    }
    return {
      protein: formData.target_protein_grams || 0,
      carbs: formData.target_carbs_grams || 0,
      fat: formData.target_fat_grams || 0
    };
  };

  const handleGenerate = async () => {
    if (!formData.plan_name.trim()) {
      alert('Please enter a plan name');
      return;
    }

    setLoading(true);
    setLoadingProgress(0);
    
    try {
      const macros = calculateMacros();
      
      const payload = {
        plan_name: formData.plan_name,
        plan_type: formData.plan_type,
        duration_days: formData.duration_days,
        target_calories_daily: formData.target_calories_daily,
        bmr_baseline: formData.use_bmr ? formData.bmr_baseline : null,
        activity_multiplier: formData.activity_multiplier,
        calorie_adjustment_goal: formData.calorie_adjustment_goal,
        
        target_protein_grams: macros.protein,
        target_carbs_grams: macros.carbs,
        target_fat_grams: macros.fat,
        target_protein_percentage: formData.target_protein_percentage,
        target_carbs_percentage: formData.target_carbs_percentage,
        target_fat_percentage: formData.target_fat_percentage,
        
        target_fiber_grams: formData.include_micros ? formData.target_fiber_grams : null,
        target_sodium_mg: formData.include_micros ? formData.target_sodium_mg : null,
        target_potassium_mg: formData.include_micros ? formData.target_potassium_mg : null,
        target_calcium_mg: formData.include_micros ? formData.target_calcium_mg : null,
        target_iron_mg: formData.include_micros ? formData.target_iron_mg : null,
        target_vitamin_d_iu: formData.include_micros ? formData.target_vitamin_d_iu : null,
        target_vitamin_c_mg: formData.include_micros ? formData.target_vitamin_c_mg : null,
        
        meals_per_day: formData.meals_per_day,
        snacks_per_day: formData.snacks_per_day,
        meal_timing_strategy: formData.meal_timing_strategy,
        eating_window_start: formData.meal_timing_strategy !== 'flexible' ? formData.eating_window_start : null,
        eating_window_end: formData.meal_timing_strategy !== 'flexible' ? formData.eating_window_end : null,
        
        target_water_ml: formData.target_water_ml,
        electrolyte_needs: formData.electrolyte_needs,
        
        diet_category: formData.diet_category,
        cuisine_preferences: formData.cuisine_preferences,
        recipe_source_preference: formData.recipe_source_preference,
        
        meal_prep_batch_size: formData.meal_prep_batch_size,
        
        monitor_weight: formData.monitor_weight,
        monitor_body_composition: formData.monitor_body_composition,
        monitor_energy_levels: formData.monitor_energy_levels,
        monitor_digestion: formData.monitor_digestion,
        recalibration_frequency_days: formData.recalibration_frequency_days,
        
        ai_generated: true,
        ai_prompt: `Generate ${formData.duration_days}-day meal plan for ${formData.plan_type} with creativity level ${formData.variety}`,
        generation_context: {
          temperature: formData.variety,
          duration_days: formData.duration_days,
          user_preferences_included: formData.use_saved_preferences
        },
        
        is_active: formData.is_active,
        start_date: formData.start_date,
        end_date: formData.end_date,
        fitness_program_id: formData.fitness_program_id
      };

      // Use fetch with SSE streaming
      const response = await authFetch(`${API_BASE}/api/meal-plans/generate-progressive`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (await handleAIError(response)) {
        return;
      }

      if (!response.ok) {
        // B-CREDIT1 (2026-10-28): the generation limits refuse with a plain-language detail.message
        // (409 already running · 429 failure limit · 503 platform paused). Show it, not a status code.
        let msg = `HTTP error! status: ${response.status}`;
        try {
          const body = await response.json();
          const d = body?.detail;
          if (d && typeof d === 'object' && d.message) msg = d.message;
          else if (typeof d === 'string') msg = d;
        } catch (_) { /* non-JSON error body: keep the status message */ }
        throw new Error(msg);
      }

      // Read the streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE messages
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            // Parse in its own try; handle events outside it, so the server's {"type":"error"} event
            // reaches the outer catch instead of being swallowed as a "parse error" (Dev audit F-9).
            let data;
            try {
              data = JSON.parse(line.slice(6));
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
              continue;
            }
            {
              if (data.type === 'progress') {
                setLoadingProgress(data.progress_percentage || 0);
                if (data.message) {
                  setProgressMessage(data.message);
                }
              } else if (data.type === 'prompt_data') {
                // XAI: Store prompt data for later display
                if (onPromptData) {
                  onPromptData(data.prompt_data);
                }
              } else if (data.type === 'complete') {
                setLoadingProgress(100);
                setProgressMessage('Complete!');
                setLoading(false);

                /* SKELETON-FIRST (2026-10-07). The plan is finished and safe; the recipe CARDS —
                   quantities, instructions, images — are being generated behind it. Measured: 70
                   cards at 8-way concurrency ~63s.

                   The old success alert claimed "Shopping list: N items to buy" unconditionally.
                   In skeleton mode there is no shopping list yet (`shopping_list_deferred`), so
                   saying that would be telling the user something untrue about their own data —
                   and the number would be 0. The server now reports the distinction explicitly
                   and the UI reports what is actually true. */
                /* A MEAL PLAN SHORT BY N MEALS IS NEVER AN ACCEPTABLE OUTCOME, and the user is
                   never asked to fix it. By the time this is non-zero the server has exhausted
                   FIVE layers on its own: two batch top-up rounds, the pre-screened catalog,
                   three focused single-meal regenerations per slot at rising temperature, and
                   finally repeating a meal already in this plan. The last one can only fail if
                   the plan contains ZERO safe meals — i.e. the whole plan failed, not a slot.
                   So this is a FAILURE message, not a "mostly fine" one. */
                const _missing = Number(data.incomplete_slots || 0);
                if (_missing > 0) {
                  alert(`We could not build a complete plan that is safe for your health ` +
                        `profile.\n\n` +
                        `${_missing} meal${_missing === 1 ? '' : 's'} could not be filled from ` +
                        `any source — new recipes, your library, or the shared library — without ` +
                        `conflicting with your conditions, medications or allergies.\n\n` +
                        `Nothing unsafe was included. Widening your recipe preferences, or ` +
                        `reviewing the restrictions on your health profile, will let us complete ` +
                        `it.`);
                }

                /* SKELETON MODE: the plan is finished and safe. Recipe cards are built WHEN A
                   MEAL IS OPENED — the server no longer pre-generates all 70 cards and images on
                   the chance someone looks at them. So there is no fill to watch: say what the
                   user has and get out of their way. (The old card-progress panel polled
                   /cards/status for a background fill that no longer runs, and would have spun
                   at 0 forever.) */
                if (data.shopping_list_deferred) {
                  alert(`Your ${data.days_generated}-day meal plan is ready.\n\n` +
                        `Full recipe details — ingredients, quantities and instructions — are ` +
                        `created the first time you open a meal.\n\n` +
                        `Your shopping list is built from those details, so you can generate it ` +
                        `once you have picked the meals you want.`);
                  if (onSuccess) onSuccess();
                  return;
                }

                alert(`Meal plan generated successfully!\n\n` +
                      `${data.days_generated} days with ${data.recipes_created} recipes\n` +
                      `Shopping list: ${data.shopping_items_count} items to buy\n` +
                      `Items already in pantry: ${data.items_in_pantry || 0}`);

                // Call onSuccess to close all modals and refresh data
                if (onSuccess) {
                  onSuccess();
                }
                return;
              } else if (data.type === 'error') {
                throw new Error(data.message || 'The meal plan could not be generated.');
              }
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Error:', error);
      alert(`Failed to generate: ${error.message}`);
    } finally {
      setLoading(false);
      setLoadingProgress(0);
    }
  };

  const cuisineOptions = [
    { value: 'no_preference', label: 'No Preference' },
    { value: 'italian', label: 'Italian' },
    { value: 'latin', label: 'Latin' },
    { value: 'asian', label: 'Asian' },
    { value: 'indian', label: 'Indian' },
    { value: 'mediterranean', label: 'Mediterranean' },
    { value: 'american', label: 'American' },
    { value: 'french', label: 'French' },
    { value: 'kosher', label: 'Kosher' }
  ];

  const toggleCuisine = (cuisine) => {
    setFormData(prev => ({
      ...prev,
      cuisine_preferences: prev.cuisine_preferences.includes(cuisine)
        ? prev.cuisine_preferences.filter(c => c !== cuisine)
        : [...prev.cuisine_preferences, cuisine]
    }));
  };

  const macros = calculateMacros();
  const macroTotal = formData.target_protein_percentage + formData.target_carbs_percentage + formData.target_fat_percentage;
  
  // Loading Modal with Animated Progress Bar
  if (loading) {
    return (
      <>
        <style>
          {`
            @keyframes progressStripes {
              0% { background-position: 0 0; }
              100% { background-position: 40px 0; }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.7; }
            }
          `}
        </style>
        <div className="mpg-loadingOverlay">
          <div className="mpg-loadingCard">
            {/* Animated Icon */}
            <div className="mpg-loadingIcon">
            </div>

            <h3 className="mpg-loadingTitle">
              Generating Your Meal Plan
            </h3>

            {/* Progress Bar Container */}
            <div className="mpg-progressBarTrack">
              {/* Animated Progress Bar with Stripes */}
              <div style={{
                width: `${loadingProgress}%`,
                height: '100%',
                backgroundColor: 'var(--brand-primary)',
                backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.2) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.2) 75%, transparent 75%, transparent)',
                backgroundSize: '40px 40px',
                animation: 'progressStripes 1s linear infinite',
                transition: 'width 0.4s ease-out',
                borderRadius: '14px',
                boxShadow: '0 2px 8px rgba(30, 58, 95, 0.3)'
              }} />
            </div>
            
            {/* Progress Percentage */}
            <div className="mpg-progressPercent">
              {loadingProgress}%
            </div>

            {/* Dynamic Progress Message from Backend */}
            <div className="mpg-progressMessage">
              {progressMessage}
            </div>

            {/* Status Indicator Dots */}
            <div className="mpg-statusDots">
              {/* DROPPED 'as_guides' 2026-10-06 (Vincent: "Drop it!"). It generated 100% of the
                  plan exactly like 'none' and differed only by one sentence in the prompt —
                  "use the saved recipes as inspiration/guides; adapt them, but generate the
                  meals" (recipe_source_service.py). Identical cost, identical time, and the
                  difference is UNVERIFIABLE: nothing can assert the model actually used them, so
                  no test could cover it. Now that Mixed delivers ~50% of the user's REAL recipes,
                  a fourth option promising unprovable influence made the control harder to use
                  for no gain. The backend still ACCEPTS the value so any stored plan carrying it
                  renders unchanged — unknown/legacy preferences already fall through to generate. */}
              {[0, 1, 2].map((i) => (
                <div key={i} className="mpg-statusDot" style={{ animationDelay: `${i * 0.3}s` }} />
              ))}
            </div>

            {/* Says what is ACTUALLY happening. The old copy promised "shopping list, and pantry
                inventory" during generation — neither of which is built here any more: the
                shopping list needs ingredient quantities, which only exist once recipe cards are
                created, so it is deliberately deferred. Telling the user we are building
                something we are not is the same class of untruth as the old completion alert. */}
            <p className="mpg-loadingDescription">
              Building your {formData.duration_days}-day plan — choosing every meal and checking
              each one against your health profile
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="mpg-formOverlay">
      <div className="mpg-formCard">
        {/* Header */}
        <div className="mpg-formHeader">
          <h3 className="mpg-formTitle">Generate AI Meal Plan <CostChip apiType={mealPlanApiType(formData.duration_days)} /></h3>
          <button onClick={onBack} className="mpg-closeBtn">×</button>
        </div>

        {/* Body */}
        <div className="mpg-formBody">
          {/* Plan Name */}
          <div className="mpg-planNameGroup">
            <label className="mpg-planNameLabel">
              Plan Name <span className="mpg-planNameHint">(e.g., "Low Carb Weight Loss Plan")</span>
            </label>
            <input
              type="text"
              value={formData.plan_name}
              onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
              placeholder="My Meal Plan"
              className="mpg-planNameInput"
            />
          </div>

          {/* Plan Type */}
          <div className="mpg-planTypeGroup">
            <label className="mpg-planTypeLabel">Plan Type</label>
            <div className="mpg-planTypeOptions">
              {[
                { value: 'weight_loss', label: 'Weight Loss (-500 cal/day)' },
                { value: 'muscle_gain', label: 'Muscle Gain (+300 cal/day)' },
                { value: 'maintenance', label: 'Maintenance' },
                { value: 'performance', label: 'Performance (High Carb)' }
              ].map(option => (
                <label key={option.value} className="mpg-planTypeOption" style={{ border: `2px solid ${formData.plan_type === option.value ? '#1E3A5F' : '#e5e7eb'}`, background: formData.plan_type === option.value ? '#EBF0F7' : 'white' }}>
                  <input
                    type="radio"
                    value={option.value}
                    checked={formData.plan_type === option.value}
                    onChange={(e) => setFormData({ ...formData, plan_type: e.target.value })}
                    className="mpg-planTypeRadio"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
          
          {/* Link to Fitness Program */}
          {fitnessPrograms.length > 0 && (
            <div className="mpg-fitnessLinkGroup">
              <label className="mpg-fitnessLinkLabel">
                Link to Fitness Program (Optional)
              </label>
              <select
                value={formData.fitness_program_id || ''}
                onChange={(e) => setFormData({ ...formData, fitness_program_id: e.target.value ? parseInt(e.target.value) : null })}
                className="mpg-fitnessLinkSelect"
              >
                <option value="">-- No Fitness Program (Standalone) --</option>
                {fitnessPrograms.map(fp => (
                  <option key={fp.id} value={fp.id}>
                    {fp.program_name} ({fp.program_duration_weeks} weeks, {fp.primary_goal?.replace('_', ' ')})
                  </option>
                ))}
              </select>
              <p className="mpg-fitnessLinkHint">
                Link this meal plan to support a specific fitness program's nutritional needs
              </p>
            </div>
          )}

          {/* Duration */}
          <div className="mpg-durationGroup">
            <label className="mpg-durationLabel">Duration</label>
            <div className="mpg-durationOptions">
              {[3, 7, 14].map(days => (
                <button
                  key={days}
                  onClick={() => setFormData({ ...formData, duration_days: days })}
                  className="mpg-durationBtn" style={{ border: `2px solid ${formData.duration_days === days ? '#1E3A5F' : '#e5e7eb'}`, background: formData.duration_days === days ? '#EBF0F7' : 'white' }}
                >
                  {days} days
                </button>
              ))}
            </div>
          </div>

          {/* Meals & Snacks */}
          <div className="mpg-mealsGrid">
            <div>
              <label className="mpg-mealsLabel">Meals per day</label>
              <select
                value={formData.meals_per_day}
                onChange={(e) => setFormData({ ...formData, meals_per_day: parseInt(e.target.value) })}
                className="mpg-mealsSelect"
              >
                {[2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="mpg-snacksLabel">Snacks per day</label>
              <select
                value={formData.snacks_per_day}
                onChange={(e) => setFormData({ ...formData, snacks_per_day: parseInt(e.target.value) })}
                className="mpg-snacksSelect"
              >
                {[0, 1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          {/* Variety Slider */}
          <div className="mpg-varietyGroup">
            <label className="mpg-varietyLabel">AI Creativity Level</label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={formData.variety}
              onChange={(e) => setFormData({ ...formData, variety: parseFloat(e.target.value) })}
              className="mpg-varietySlider"
            />
            <div className="mpg-varietyScale">
              <span>Focused (0)</span>
              <span className="mpg-varietyValue">{formData.variety.toFixed(1)}</span>
              <span>Creative (2)</span>
            </div>
          </div>

          {/* Info Box */}
          <div className="mpg-infoBox">
            <strong>Using your food preferences, recipes, and allergen restrictions</strong>
            <br />AI will personalize based on your calorie goals, saved recipes, and pantry inventory
          </div>
          
          {/* Recipe Source Preference */}
          <div className="mpg-advancedContent" style={{ marginBottom: '16px' }}>
            <div className="mpg-dietHeading">Recipe Source</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '0 8px' }}>
              {[
                /* These four values are UNCHANGED — the UI already sent them. What changed on
                   2026-10-06 is that the BACKEND now acts on them instead of only appending a text
                   directive to a prompt that generated every recipe anyway, so the timings below
                   are real: only_existing composes from the catalog with ZERO AI generation,
                   prefer_existing fills only the gaps, as_guides/none generate everything.
                   Timings are stated because a user chooses on them. Quotas are not mentioned —
                   that is our problem, not theirs. Default stays 'none' so no existing user's
                   plan silently changes behaviour. */
                /* The three options differ in HOW MUCH of the plan comes from your own recipes —
                   never in whether you end up with a complete plan. Anything your library cannot
                   fill is written for you.

                   The old copy promised things the server does not do, which is the one thing a
                   client must never do:
                     · only_existing said "ready in seconds, no AI". Measured on healist_dev
                       2026-10-07: 8 of 16 users own ZERO recipes and only 2 own the 70 a 14-day
                       plan needs, so for almost everyone AI writes most of it. For a user with no
                       recipes it is identical to All New.
                     · prefer_existing said "under a minute" against a measured 169s.
                   No timing claim is made here now, because the honest number depends on how much
                   of your library fits and no post-fix end-to-end timing has been measured. */
                { value: 'none', label: 'Use NONE of the Existing Recipes', desc: 'Every recipe written fresh for you — the slowest option' },
                /* prefer_existing draws on the SHARED, anonymized Healist Recipe Library as well as
                   the user's own recipes since 2026-10-15 (Vincent) — the copy says so, because a
                   client must never describe behaviour the server does not have. */
                { value: 'prefer_existing', label: 'Mixed — your recipes plus new ones', desc: 'About half from your saved recipes and our screened recipe library, the rest written for you' },
                { value: 'only_existing', label: 'Use ONLY My Existing Recipes', desc: 'As much as your saved recipes cover; anything left over is written for you. If you have no saved recipes yet, this gives you an all-new plan.' },
                { value: 'library_screened', label: 'Use Our Recipe Library, Screened for Your Health Context', desc: 'Our whole library filtered against your conditions, medications and allergies — works even with no saved recipes of your own' }
              ].map(option => (
                <label
                  key={option.value}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '10px 12px',
                    border: `2px solid ${formData.recipe_source_preference === option.value ? '#1E3A5F' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    background: formData.recipe_source_preference === option.value ? '#EBF0F7' : 'white',
                    cursor: 'pointer'
                  }}
                >
                  <input
                    type="radio"
                    name="recipe_source_preference"
                    value={option.value}
                    checked={formData.recipe_source_preference === option.value}
                    onChange={(e) => setFormData({ ...formData, recipe_source_preference: e.target.value })}
                    style={{ marginTop: '3px' }}
                  />
                  <div>
                    <div style={{ fontWeight: 500, fontSize: '14px', color: '#1E3A5F' }}>{option.label}</div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{option.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Advanced Options */}
          <div className="mpg-advancedSection">
            <div
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="mpg-advancedToggle"
            >
              <span>Advanced Options</span>
              <span className="mpg-advancedArrow" style={{ transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
            </div>

            {showAdvanced && (
              <div className="mpg-advancedContent">
                {/* Calorie Framework */}
                <div className="mpg-sectionHeading">
                  Calorie Framework
                </div>

                <div className="mpg-calorieGroup">
                  <label className="mpg-calorieLabel">Target Calories per Day</label>
                  <input
                    type="number"
                    value={formData.target_calories_daily}
                    onChange={(e) => setFormData({ ...formData, target_calories_daily: parseInt(e.target.value) })}
                    step="50"
                    className="mpg-calorieInput"
                  />
                </div>

                <div className="mpg-activityGrid">
                  <div>
                    <label className="mpg-activityLabel">Activity Multiplier</label>
                    <select
                      value={formData.activity_multiplier}
                      onChange={(e) => setFormData({ ...formData, activity_multiplier: parseFloat(e.target.value) })}
                      className="mpg-activitySelect"
                    >
                      <option value="1.2">1.2 - Sedentary</option>
                      <option value="1.375">1.375 - Light activity</option>
                      <option value="1.5">1.5 - Moderate</option>
                      <option value="1.725">1.725 - Very active</option>
                      <option value="1.9">1.9 - Extra active</option>
                    </select>
                  </div>
                  <div>
                    <label className="mpg-adjustmentLabel">Calorie Adjustment</label>
                    <input
                      type="number"
                      value={formData.calorie_adjustment_goal}
                      onChange={(e) => setFormData({ ...formData, calorie_adjustment_goal: parseInt(e.target.value) })}
                      placeholder="e.g., -500"
                      className="mpg-adjustmentInput"
                    />
                  </div>
                </div>

                <hr className="mpg-divider" />

                {/* Macronutrients */}
                <div className="mpg-macroHeading">
                  Macronutrient Targets
                </div>

                <div className="mpg-macroGrid">
                  <div>
                    <label className="mpg-proteinLabel">Protein (%)</label>
                    <input
                      type="number"
                      value={formData.target_protein_percentage}
                      onChange={(e) => setFormData({ ...formData, target_protein_percentage: parseFloat(e.target.value) })}
                      min="0"
                      max="100"
                      className="mpg-proteinInput"
                    />
                    <div className="mpg-proteinGrams">
                      → {macros.protein}g
                    </div>
                  </div>
                  <div>
                    <label className="mpg-carbsLabel">Carbs (%)</label>
                    <input
                      type="number"
                      value={formData.target_carbs_percentage}
                      onChange={(e) => setFormData({ ...formData, target_carbs_percentage: parseFloat(e.target.value) })}
                      min="0"
                      max="100"
                      className="mpg-carbsInput"
                    />
                    <div className="mpg-carbsGrams">
                      → {macros.carbs}g
                    </div>
                  </div>
                  <div>
                    <label className="mpg-fatLabel">Fat (%)</label>
                    <input
                      type="number"
                      value={formData.target_fat_percentage}
                      onChange={(e) => setFormData({ ...formData, target_fat_percentage: parseFloat(e.target.value) })}
                      min="0"
                      max="100"
                      className="mpg-fatInput"
                    />
                    <div className="mpg-fatGrams">
                      → {macros.fat}g
                    </div>
                  </div>
                </div>

                {macroTotal !== 100 && (
                  <div className="mpg-macroWarning">
                    Macro percentages must total 100% (currently {macroTotal}%)
                  </div>
                )}

                <hr className="mpg-dividerMacro" />

                {/* Diet Category */}
                <div className="mpg-dietHeading">
                  Diet & Preferences
                </div>

                <div className="mpg-dietGroup">
                  <label className="mpg-dietLabel">Diet Category</label>
                  <select
                    value={formData.diet_category}
                    onChange={(e) => setFormData({ ...formData, diet_category: e.target.value })}
                    className="mpg-dietSelect"
                  >
                    <option value="standard_balanced">Standard Balanced</option>
                    <option value="low_carb">Low Carb / Keto</option>
                    <option value="mediterranean">Mediterranean</option>
                    <option value="vegetarian">Vegetarian</option>
                    <option value="vegan">Vegan</option>
                    <option value="paleo">Paleo</option>
                  </select>
                </div>

                <div className="mpg-cuisineGroup">
                  <label className="mpg-cuisineLabel">Cuisine Preferences</label>
                  <div className="mpg-cuisineChips">
                    {cuisineOptions.map(cuisine => (
                      <div
                        key={cuisine.value}
                        onClick={() => toggleCuisine(cuisine.value)}
                        className="mpg-cuisineChip" style={{ border: `2px solid ${formData.cuisine_preferences.includes(cuisine.value) ? '#1E3A5F' : '#e5e7eb'}`, background: formData.cuisine_preferences.includes(cuisine.value) ? '#EBF0F7' : 'white', color: formData.cuisine_preferences.includes(cuisine.value) ? '#1E3A5F' : '#374151' }}
                      >
                        {cuisine.label}
                      </div>
                    ))}
                  </div>
                </div>

                <hr className="mpg-dividerCuisine" />

                {/* Meal Timing */}
                <div className="mpg-timingHeading">
                  Meal Timing
                </div>

                <div className="mpg-timingGroup">
                  <label className="mpg-timingLabel">Timing Strategy</label>
                  <select
                    value={formData.meal_timing_strategy}
                    onChange={(e) => setFormData({ ...formData, meal_timing_strategy: e.target.value })}
                    className="mpg-timingSelect"
                  >
                    <option value="flexible">Flexible (any time)</option>
                    <option value="intermittent_fasting_16_8">Intermittent Fasting (16:8)</option>
                    <option value="time_restricted">Time-Restricted (12hr)</option>
                  </select>
                </div>

                {formData.meal_timing_strategy !== 'flexible' && (
                  <div className="mpg-windowGrid">
                    <div>
                      <label className="mpg-windowStartLabel">Window Start</label>
                      <input
                        type="time"
                        value={formData.eating_window_start}
                        onChange={(e) => setFormData({ ...formData, eating_window_start: e.target.value })}
                        className="mpg-windowStartInput"
                      />
                    </div>
                    <div>
                      <label className="mpg-windowEndLabel">Window End</label>
                      <input
                        type="time"
                        value={formData.eating_window_end}
                        onChange={(e) => setFormData({ ...formData, eating_window_end: e.target.value })}
                        className="mpg-windowEndInput"
                      />
                    </div>
                  </div>
                )}

                <hr className="mpg-dividerTiming" />

                {/* Hydration & Monitoring */}
                <div className="mpg-hydrationHeading">
                  Hydration & Monitoring
                </div>

                <div className="mpg-hydrationGrid">
                  <div>
                    <label className="mpg-waterLabel">Daily Water (ml)</label>
                    <input
                      type="number"
                      value={formData.target_water_ml}
                      onChange={(e) => setFormData({ ...formData, target_water_ml: parseInt(e.target.value) })}
                      step="100"
                      className="mpg-waterInput"
                    />
                  </div>
                  <div>
                    <label className="mpg-batchLabel">Meal Prep Batch</label>
                    <select
                      value={formData.meal_prep_batch_size}
                      onChange={(e) => setFormData({ ...formData, meal_prep_batch_size: parseInt(e.target.value) })}
                      className="mpg-batchSelect"
                    >
                      <option value="1">Daily prep</option>
                      <option value="7">1 week</option>
                      <option value="14">2 weeks</option>
                    </select>
                  </div>
                </div>

                <div className="mpg-monitorGroup">
                  <label className="mpg-monitorLabel">Track Progress Via:</label>
                  {[
                    { key: 'monitor_weight', label: 'Weight measurements' },
                    { key: 'monitor_body_composition', label: 'Body composition' },
                    { key: 'monitor_energy_levels', label: 'Energy levels' },
                    { key: 'monitor_digestion', label: 'Digestion quality' }
                  ].map(item => (
                    <label key={item.key} className="mpg-monitorOption">
                      <input
                        type="checkbox"
                        checked={formData[item.key]}
                        onChange={(e) => setFormData({ ...formData, [item.key]: e.target.checked })}
                        className="mpg-monitorCheckbox"
                      />
                      {item.label}
                    </label>
                  ))}
                </div>

                <div>
                  <label className="mpg-recalLabel">Recalibration Frequency (days)</label>
                  <input
                    type="number"
                    value={formData.recalibration_frequency_days}
                    onChange={(e) => setFormData({ ...formData, recalibration_frequency_days: parseInt(e.target.value) })}
                    min="7"
                    max="90"
                    className="mpg-recalInput"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Active Plan Checkbox */}
          <div className="mpg-activeGroup">
            <label className="mpg-activeLabel">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="mpg-activeCheckbox"
              />
              Make this my active meal plan
            </label>
          </div>

          <MedicalDisclaimer variant="ai" />

          {/* Generate Button */}
          <button
            onClick={() => spend(mealPlanApiType(formData.duration_days), 'Generate this meal plan?', handleGenerate)}
            disabled={loading || macroTotal !== 100}
            className="mpg-generateBtn" style={{ background: loading || macroTotal !== 100 ? '#D1D5DB' : 'linear-gradient(135deg, #1E3A5F 0%, #0F1C2E 100%)', cursor: loading || macroTotal !== 100 ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'Generating AI Meal Plan...' : <>Generate AI Meal Plan <CostChip apiType={mealPlanApiType(formData.duration_days)} /></>}
          </button>
        </div>
      </div>
    </div>
  );

  // Show generated plan details
  /* ── CARD-BY-CARD FILL STATUS ─────────────────────────────────────────────────────────────
     Shown after a skeleton plan is delivered. The plan is already complete and already safe —
     every meal passed the UHC gate before this screen appeared. What is still happening is the
     recipe DETAIL: quantities, instructions and an image per meal, each stored permanently so
     the shared catalog grows and later plans come from storage instead of generation.

     The user can leave at any time. The fill continues server-side, and
     POST /{plan_id}/cards/fill finishes anything a restart interrupted. */
  if (cardProgress) {
    const total = cardProgress.total;
    const ready = cardProgress.ready || 0;
    const pct = total ? Math.round((ready / total) * 100) : 0;
    const done = Boolean(cardProgress.complete);
    return (
      <div className="mpg-resultOverlay">
        <div className="mpg-resultCard">
          <div className="mpg-resultHeader">
            <h3 className="mpg-resultTitle">
              {done ? 'Meal Plan Ready' : 'Meal Plan Ready — Preparing Recipes'}
            </h3>
          </div>
          <div className="mpg-resultBody">
            <p className="mpg-resultDetailItem">
              Your plan is complete and every meal has been checked against your health profile.
              {done
                ? ' All recipe details are ready.'
                : ' Recipe details and photos are being prepared — you can close this and come back.'}
            </p>

            <div style={{ margin: '14px 0' }}>
              <div className="mpg-progressBarTrack">
                <div style={{ height: '100%', width: `${pct}%`, background: '#1E3A5F',
                              transition: 'width .4s ease' }} />
              </div>
              <div className="mpg-resultDetailItem" style={{ marginTop: 6 }}>
                {total == null ? 'Starting…' : `${ready} of ${total} recipes ready`}
              </div>
            </div>

            {cardProgress.items && cardProgress.items.length > 0 && (
              <div className="mpg-shoppingList" style={{ maxHeight: 260, overflowY: 'auto' }}>
                {cardProgress.items.map((it) => (
                  <div key={it.schedule_id} className="mpg-shoppingItem"
                       style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span aria-hidden="true" style={{ width: 16, flex: '0 0 16px' }}>
                      {it.has_card ? '\u2713' : '\u00B7'}
                    </span>
                    {it.image_url && (
                      <img src={it.image_url} alt="" width="28" height="28"
                           style={{ borderRadius: 4, objectFit: 'cover' }} />
                    )}
                    <span style={{ flex: 1 }}>
                      Day {it.day_number} · {it.meal_type} — {it.meal_name}
                    </span>
                    <span className="mpg-resultDetailItem" style={{ fontSize: 12 }}>
                      {it.has_card ? 'ready' : 'preparing'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <p className="mpg-resultDetailItem" style={{ marginTop: 12, fontSize: 13 }}>
              A shopping list or grocery order needs ingredient quantities, so it becomes
              available once these are finished. Pre-prepared meal matching is available now.
            </p>

            <button className="mpg-doneBtn"
                    onClick={() => { setCardProgress(null); if (onSuccess) onSuccess(); }}>
              {done ? 'View My Plan' : 'Close — keep preparing in the background'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showPlanDetails && generatedPlan) {
    return (
      <div className="mpg-resultOverlay">
        <div className="mpg-resultCard">
          {/* Header */}
          <div className="mpg-resultHeader">
            <h3 className="mpg-resultTitle">
              Meal Plan Generated!
            </h3>
          </div>

          {/* Body */}
          <div className="mpg-resultBody">
            <div className="mpg-resultSummary">
              <h4 className="mpg-resultPlanName">
                {generatedPlan.plan.plan_name}
              </h4>
              <div className="mpg-resultDetails">
                <p className="mpg-resultDetailItem">Duration: {formData.duration_days} days</p>
                <p className="mpg-resultDetailItem">Daily Target: {generatedPlan.plan.target_calories} calories</p>
                <p className="mpg-resultDetailItem">Start: {formatDateOnly(generatedPlan.plan.start_date)}</p>
                <p className="mpg-resultDetailItem">End: {formatDateOnly(generatedPlan.plan.end_date)}</p>
              </div>
            </div>

            {/* Shopping List */}
            {generatedPlan.shopping_list && generatedPlan.shopping_list.length > 0 && (
              <div className="mpg-shoppingSection">
                <h4 className="mpg-shoppingTitle">Shopping List</h4>
                <div className="mpg-shoppingList">
                  {generatedPlan.shopping_list.map((item, idx) => (
                    <div key={idx} className="mpg-shoppingItem" style={{ borderBottom: idx < generatedPlan.shopping_list.length - 1 ? '1px solid #E5E7EB' : 'none' }}>
                      <strong>{item.item}</strong> - {item.quantity}
                      {item.category && <span className="mpg-shoppingCategory"> ({item.category})</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Meal Prep Tips */}
            {generatedPlan.meal_prep_tips && generatedPlan.meal_prep_tips.length > 0 && (
              <div className="mpg-prepTipsSection">
                <h4 className="mpg-prepTipsTitle">Meal Prep Tips</h4>
                <ul className="mpg-prepTipsList">
                  {generatedPlan.meal_prep_tips.map((tip, idx) => (
                    <li key={idx} className="mpg-prepTipItem">{tip}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mpg-resultInfoBox">
              <strong>Your complete meal plan is ready!</strong>
              <br />
              View specific meals and details in <strong>Nutrition → Meal Plan Management</strong>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="mpg-doneBtn"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default MealPlanGenerator;